import { useUpdateRoleMutation } from 'api/RolesAPI';
import { Location } from 'history';
import { EditableHeaderInput } from 'modules/roles/components/details/EditableHeaderInput';
import { useRolesContext, useRolesModalsContext } from 'modules/roles/contexts';
import { CombinedRole } from 'modules/roles/types';
import { createNavLinks, createRolesOptions } from 'modules/roles/utils';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useLocation, useParams } from 'react-router';
import { ApiError } from 'services/api';
import { Spinner } from 'ui/spinner';
import { SideNav } from 'ui/sideNav';
import { FixedWidth } from 'ui/utility/fixedWidth';
import { useScrollIntoView } from 'utils/hooks/useScrollIntoView';
import { RolesModals } from '../index/RolesModals';
import { PageActions } from './PageActions';
import { Permissions } from './Permissions';
import { Layout, StyledFullWidth, StyledSecondaryPageHeader } from './RoleDetailsPage.styles';

export const DETAILS_PAGE_TEXT = {
  breadcrumb: 'Roles',
};

export const RoleDetailsPage = () => {
  const location = useLocation();
  const {
    currentRole = { name: 'Name' } as CombinedRole,
    isLoadingRoles,
    combinedRoles,
    rolesById,
    refetchRoles,
  } = useRolesContext();
  const { showUnsavedChangesModal, hideModal } = useRolesModalsContext();
  const dispatch = useDispatch();
  const { roleId } = useParams<{ roleId: string }>();
  const [displayText, setDisplayText] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isHeaderDirty, setIsHeaderDirty] = useState(false);
  const [isPermissionsDirty, setIsPermissionsDirty] = useState(false);
  const [permissions, setPermissions] = useState(currentRole?.permissions || []);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const [nextPath, setNextPath] = useState('');
  const history = useHistory();
  useScrollIntoView();

  const { mutate } = useUpdateRoleMutation({
    onError: (e: unknown) => {
      setIsEditMode(false);
      setDisplayText(currentRole.name);
      const errorText = (e as ApiError)?.response?.data?.message;
      dispatch(addNegativeToast(errorText));
    },
  });

  useEffect(() => {
    if (currentRole) {
      setDisplayText(currentRole.name);
      setPermissions(currentRole.permissions);
    }
  }, [currentRole?.name, currentRole?.permissions]);

  useEffect(() => {
    if (!isHeaderDirty && !isPermissionsDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isHeaderDirty, isPermissionsDirty]);

  useEffect(() => {
    // redirects once action button other than Cancel or Close
    // has been choosen
    if (allowNavigation && !isHeaderDirty && !isPermissionsDirty) {
      history.push(nextPath);
    }
  }, [allowNavigation, isHeaderDirty, isPermissionsDirty]);

  /**
   * Copy permissions from another role
   */
  const handleCopyRoles = (selectedRole: string) => {
    setIsPermissionsDirty(true);
    setPermissions(rolesById[selectedRole].permissions);
    dispatch(
      addPositiveToast(
        `Permissions copied from ${rolesById[selectedRole].name}. Changes are unsaved.`,
      ),
    );
  };

  /**
   * Update permssions on button click
   */
  const handleUpdatePermissions = () => {
    const payload = {
      id: currentRole.id,
      name: currentRole.name,
      permissions,
    };
    mutate(payload, {
      onSuccess: () => {
        setIsPermissionsDirty(false);
        refetchRoles();
        hideModal();
        dispatch(addPositiveToast('Success! Role permissions updated'));
        if (nextPath) setAllowNavigation(true);
      },
    });
  };

  /**
   * Update name on blur or enter
   */
  const handleUpdateName = () => {
    setIsHeaderDirty(false);
    setIsEditMode(false);
    setDisplayText(displayText || currentRole.name);

    if (displayText !== currentRole.name && displayText !== '') {
      const payload = {
        id: currentRole.id,
        name: displayText,
        permissions: currentRole.permissions,
      };
      mutate(payload, {
        onSuccess: () => {
          setIsHeaderDirty(false);
          refetchRoles();
          hideModal();
          dispatch(addPositiveToast('Success! Role name updated'));
          if (nextPath) setAllowNavigation(true);
        },
      });
    }
  };

  /**
   * Handles Header Name changes
   */
  const handleNameChange = (value: string) => {
    if (value !== displayText && !isHeaderDirty) {
      setIsHeaderDirty(true);
    }
    setDisplayText(value);
  };

  /** Handles blocking React Routes */
  const handleBlockedNavigation = (nextLocation: Location): boolean => {
    setNextPath(nextLocation.pathname);
    const isDirty = isHeaderDirty || isPermissionsDirty;
    const isNewLocation = location.pathname !== nextLocation.pathname;
    if (isDirty && isNewLocation) {
      showUnsavedChangesModal();
      return false;
    }
    return true;
  };

  const navLinks = createNavLinks(roleId);
  const options = createRolesOptions(combinedRoles);

  const handleClosingModal = () => {
    hideModal();
    setIsHeaderDirty(false);
    setIsPermissionsDirty(false);
    setAllowNavigation(true);
  };

  const handleSetPermissions = (updatePermission: any) => {
    setIsPermissionsDirty(true);
    setPermissions((prev) => updatePermission(prev));
  };

  return isLoadingRoles ? (
    <Spinner />
  ) : (
    <>
      <Prompt message={handleBlockedNavigation} />
      <RolesModals
        onDiscard={handleClosingModal}
        onSave={() => {
          handleUpdateName();
          handleUpdatePermissions();
        }}
      />
      <StyledFullWidth>
        <FixedWidth>
          <StyledSecondaryPageHeader
            documentTitle={`${displayText} | Roles`}
            breadcrumb={{ href: '/roles', content: DETAILS_PAGE_TEXT.breadcrumb }}
            pageActions={
              <PageActions
                onChange={handleCopyRoles}
                onClick={handleUpdatePermissions}
                options={options}
              />
            }
          >
            <EditableHeaderInput
              isEditMode={isEditMode}
              onBlur={handleUpdateName}
              onChange={handleNameChange}
              onClick={() => setIsEditMode(true)}
              value={displayText}
            />
          </StyledSecondaryPageHeader>
        </FixedWidth>
      </StyledFullWidth>
      <FixedWidth>
        <Layout>
          <SideNav
            isSticky
            stickyPadding={20}
            linksAndSections={[{ header: 'Permission groups', links: navLinks }]}
          />
          <Permissions
            isLoading={isLoadingRoles}
            permissions={permissions}
            setPermissions={handleSetPermissions}
          />
        </Layout>
      </FixedWidth>
    </>
  );
};
